import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Truck, Plus, Search, Upload, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VehicleTypeSelect } from "@/components/VehicleTypes";
import { SERVICE, VEHICLE_STATUS, fmtDate, fmtDateTime } from "@/lib/util";
import { Loader, Pill, Field, Section, FileButton, VehNo } from "@/components/common";
import Modal from "@/components/Modal";
import DocUploadModal from "@/components/DocUploadModal";
import { PageTitle, Table, Td, DriverPicker } from "@/pages/admin/ui";
import DateInput from "@/components/DateInput";

const EMPTY = { admin_number: "", plate: "", type: "truck", make: "", model: "", year: "", status: "active" };

function Lookup({ plate: initial, onAssign, onEdit }) {
  const { can } = useAuth();
  const [plate, setPlate] = useState(initial || "");
  const [date, setDate] = useState("");
  const [r, setR] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docOpen, setDocOpen] = useState(false);
  const search = useCallback(async (pl = plate) => {
    if (!pl.trim()) return;
    try {
      const { data } = await api.get("/vehicles/lookup", { params: { plate: pl, date } });
      setR(data);
      if (data.found) setDocs((await api.get(`/vehicles/${data.vehicle.id}`)).data.documents);
    } catch (e) { toast.error(errMsg(e)); }
  }, [plate, date]);
  useEffect(() => { if (initial) search(initial); }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Section title="البحث برقم المركبة (الإداري أو العادي)">
      <div className="flex flex-wrap gap-2 items-end">
        <Field label="الرقم الإداري أو العادي"><input data-testid="vehicle-search-plate-input" className="field field-sm ltr text-left" value={plate} onChange={(e) => setPlate(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} /></Field>
        <Field label="بتاريخ (اختياري)"><DateInput testid="vehicle-search-date" className="field field-sm" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <button data-testid="vehicle-search-btn" onClick={() => search()} className="btn btn-md btn-dark"><Search className="w-5 h-5" />بحث</button>
      </div>
      {r && !r.found && <p className="mt-4 text-red-600 font-bold" data-testid="vehicle-not-found">لا توجد شاحنة بهذا الرقم{r.candidates?.length ? ` (مطابقات: ${r.candidates.map((c) => `${c.admin_number || "—"}/${c.plate}`).join("، ")})` : ""}</p>}
      {r?.found && (
        <div className="mt-5 grid md:grid-cols-3 gap-4" data-testid="vehicle-lookup-result">
          <div className="rounded-2xl bg-slate-50 p-4">
            <div className="text-sm text-slate-500">المركبة</div>
            <div className="my-2"><VehNo adm={r.vehicle.admin_number} plate={r.vehicle.plate} testid="lookup-vehicle-numbers" /></div>
            <div>{SERVICE[r.vehicle.type]} • {r.vehicle.make} {r.vehicle.model} {r.vehicle.year}</div>
            <Pill color={r.vehicle.status === "active" ? "green" : "orange"}>{VEHICLE_STATUS[r.vehicle.status] || r.vehicle.status}</Pill>
            {can("vehicles_manage") && <button data-testid="assign-driver-btn" onClick={() => onAssign(r.vehicle, () => search())} className="btn btn-sm btn-blue mt-3 w-full">تغيير السائق المرتبط</button>}
            {can("vehicles_manage") && <button data-testid="lookup-edit-vehicle-btn" onClick={() => onEdit(r.vehicle, (nv) => search(nv.admin_number || nv.plate))} className="btn btn-sm btn-ghost mt-2 w-full"><Pencil className="w-4 h-4" />تعديل بيانات المركبة</button>}
          </div>
          <div className="rounded-2xl bg-blue-50 p-4" data-testid="lookup-current-driver">
            <div className="text-sm text-slate-500">السائق الحالي</div>
            {r.current_driver ? <Link to={`/admin/drivers/${r.current_driver.id}`} className="block text-xl font-black text-blue-800">{r.current_driver.full_name} <span className="ltr text-base">{r.current_driver.serial}</span></Link> : <div className="font-bold">لا يوجد</div>}
            {r.vehicle.assigned_since && <div className="text-sm text-slate-600">منذ {fmtDateTime(r.vehicle.assigned_since)}</div>}
          </div>
          <div className="rounded-2xl bg-amber-50 p-4" data-testid="lookup-driver-at-date">
            <div className="text-sm text-slate-500">السائق بتاريخ {date ? fmtDate(date) : "(اختر تاريخاً)"}</div>
            {date ? (r.driver_at_date ? <Link to={`/admin/drivers/${r.driver_at_date.id}`} className="block text-xl font-black">{r.driver_at_date.full_name} <span className="ltr text-base">{r.driver_at_date.serial}</span></Link> : <div className="font-bold">لا يوجد سائق مرتبط بهذا التاريخ</div>) : "—"}
            {date && r.driver_at_date && r.current_driver && r.driver_at_date.id !== r.current_driver.id && <Pill color="orange">مختلف عن السائق الحالي</Pill>}
          </div>
          <div className="md:col-span-2">
            <div className="font-bold mb-2">سجل السائقين على هذه الشاحنة</div>
            <div className="space-y-2" data-testid="vehicle-history">{r.history.map((h) => (
              <div key={h.id} className="flex justify-between rounded-xl border p-3 text-sm"><span className="font-bold">{h.driver?.full_name} <span className="ltr text-slate-500">{h.driver?.serial}</span></span>
                <span>{fmtDateTime(h.start_at)} ← {h.end_at ? fmtDateTime(h.end_at) : "حتى الآن"}</span></div>))}</div>
          </div>
          <div>
            <div className="flex justify-between items-center mb-2"><b>مستندات الشاحنة</b>{can("documents_manage") && <button data-testid="vehicle-doc-upload" onClick={() => setDocOpen(true)} className="btn btn-sm btn-green"><Upload className="w-4 h-4" />رفع</button>}</div>
            {docs.map((d) => <div key={d.id} className="flex justify-between items-center border rounded-xl p-2 mb-2 text-sm"><span>{d.name} • {fmtDate(d.expiry_date)}</span><FileButton fileId={d.file_id} label="عرض" className="btn btn-sm btn-ghost" testid={`vdoc-${d.id}`} /></div>)}
            {docs.length === 0 && <p className="text-sm text-slate-500">لا توجد مستندات</p>}
          </div>
          <DocUploadModal open={docOpen} onClose={() => setDocOpen(false)} vehicleId={r.vehicle.id} onSaved={() => search()} />
        </div>
      )}
    </Section>
  );
}

export default function Vehicles() {
  const { can } = useAuth();
  const [sp] = useSearchParams();
  const [list, setList] = useState(null);
  const [add, setAdd] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const [assign, setAssign] = useState(null);
  const [sel, setSel] = useState([]);
  const [start, setStart] = useState("");
  const load = () => api.get("/vehicles").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (edit) {
        const { data } = await api.patch(`/vehicles/${edit.v.id}`, f);
        toast.success("تم حفظ تعديلات المركبة");
        edit.after && edit.after(data);
      } else {
        await api.post("/vehicles", f);
        toast.success("تمت إضافة الشاحنة");
      }
      setAdd(false); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openAdd = () => { setEdit(null); setF(EMPTY); setAdd(true); };
  const openEdit = (v, after) => {
    setEdit({ v, after });
    setF({ admin_number: v.admin_number || "", plate: v.plate || "", type: v.type === "tow" ? "flatbed" : v.type || "truck", make: v.make || "", model: v.model || "", year: v.year || "", status: v.status || "active" });
    setAdd(true);
  };
  const doAssign = async (unlink) => {
    try {
      await api.post(`/vehicles/${assign.v.id}/assign`, { driver_id: unlink ? null : sel[0]?.id, start_at: start ? new Date(start).toISOString() : null });
      toast.success(unlink ? "تم فك الارتباط" : "تم ربط السائق بالشاحنة");
      assign.after && assign.after();
      setAssign(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageTitle title="الشاحنات والمركبات" sub="شاحنات الشركة فقط (وليست السيارات المنقولة)" icon={Truck}>
        {can("vehicles_manage") && <button data-testid="add-vehicle-btn" onClick={openAdd} className="btn btn-md btn-blue"><Plus className="w-5 h-5" />إضافة مركبة</button>}
      </PageTitle>
      <div className="space-y-5">
        <Lookup plate={sp.get("plate")} onAssign={(v, after) => { setSel([]); setStart(""); setAssign({ v, after }); }} onEdit={openEdit} />
        {!list ? <Loader /> : (
          <Table head={["رقم المركبة", "النوع", "الماركة / الموديل", "الحالة", "السائق الحالي", "منذ", ""]} testid="vehicles-table">
            {list.map((v) => <tr key={v.id}>
              <Td><VehNo adm={v.admin_number} plate={v.plate} /></Td><Td>{SERVICE[v.type]}</Td><Td>{v.make} {v.model} {v.year}</Td>
              <Td><Pill color={v.status === "active" ? "green" : "orange"}>{VEHICLE_STATUS[v.status] || v.status}</Pill></Td>
              <Td>{v.driver ? <Link to={`/admin/drivers/${v.driver.id}`} className="text-blue-700 font-bold">{v.driver.full_name} <span className="ltr">{v.driver.serial}</span></Link> : "—"}</Td>
              <Td>{fmtDate(v.assigned_since)}</Td>
              <Td>{can("vehicles_manage") && <div className="flex gap-1">
                <button data-testid={`edit-vehicle-${v.id}`} onClick={() => openEdit(v)} className="btn btn-sm btn-ghost"><Pencil className="w-4 h-4" />تعديل</button>
                <button data-testid={`assign-${v.plate}`} onClick={() => { setSel([]); setStart(""); setAssign({ v }); }} className="btn btn-sm btn-ghost">ربط سائق</button>
              </div>}</Td>
            </tr>)}
          </Table>
        )}
      </div>
      <Modal open={add} onClose={() => { setAdd(false); setEdit(null); }} title={edit ? "تعديل بيانات المركبة" : "إضافة مركبة"} testid="add-vehicle-modal">
        {edit && <p className="text-sm text-slate-500">يتم تحديث الرقم في المخالفات وسجل الربط. الفواتير والنقلات السابقة تبقى بالرقم المسجّل وقت إصدارها.</p>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="الرقم الإداري (رقمان)" required><input data-testid="nv-admin-number" className="field ltr text-left" inputMode="numeric" maxLength={2} value={f.admin_number} onChange={(e) => setF({ ...f, admin_number: e.target.value.replace(/\D/g, "").slice(0, 2) })} /></Field>
          <Field label="الرقم العادي (اللوحة)" required><input data-testid="nv-plate" className="field ltr text-left" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value })} /></Field>
        </div>
        <Field label="النوع"><VehicleTypeSelect testid="nv-type" value={f.type} onChange={(v) => setF({ ...f, type: v })} /></Field>
        <div className="grid grid-cols-3 gap-2">
          {[["make", "الماركة"], ["model", "الموديل"], ["year", "السنة"]].map(([k, l]) => <Field key={k} label={l}><input data-testid={`nv-${k}`} className="field field-sm" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>)}
        </div>
        <Field label="الحالة"><select data-testid="nv-status" className="field" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>{Object.entries(VEHICLE_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        <button data-testid="nv-save" onClick={save} className="btn btn-lg btn-blue w-full">{edit ? "حفظ التعديلات" : "حفظ"}</button>
      </Modal>
      <Modal open={!!assign} onClose={() => setAssign(null)} title={`ربط سائق بالمركبة ${assign ? [assign.v.admin_number, assign.v.plate].filter(Boolean).join("/") : ""}`} testid="assign-modal">
        <p className="text-sm text-slate-500">سيتم إنهاء ارتباط السائق السابق وحفظ التاريخ في السجل.</p>
        <DriverPicker selected={sel} onChange={setSel} testid="assign-driver-picker" />
        <Field label="تاريخ بداية الارتباط (اختياري، الافتراضي الآن)"><input data-testid="assign-start" type="datetime-local" className="field field-sm" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
        <button data-testid="assign-save" disabled={!sel.length} onClick={() => doAssign(false)} className="btn btn-lg btn-blue w-full">ربط السائق</button>
        {assign?.v.current_driver_id && <button data-testid="unassign-btn" onClick={() => doAssign(true)} className="btn btn-md btn-ghost w-full">فك الارتباط بدون سائق جديد</button>}
      </Modal>
    </div>
  );
}
